import React, { useState, useEffect } from 'react';
import { 
    Building2, Users, Settings, Shield, ChevronRight, 
    Plus, Edit2, Trash2, Eye, X, Loader2, 
    Globe, Database, Activity, AlertTriangle,
    LogIn, BarChart3, Megaphone, Mail, Lock, Calculator, FlaskConical
} from 'lucide-react';
import { apiFetch, setAccessToken } from '../utils/api';
import ScoringStrategyManager from './ScoringStrategyManager';

/**
 * SuperAdminDashboard - Multi-tenant management for SuperAdmins
 * Uses apiFetch to include Authorization Bearer token
 * Validates session on load and requires re-auth if expired
 */
const SuperAdminDashboard = ({ user, onLoginAsClub }) => {
    const [clubs, setClubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('clubs');
    const [selectedClub, setSelectedClub] = useState(null);
    const [showClubForm, setShowClubForm] = useState(false);
    const [systemStats, setSystemStats] = useState(null);
    const [scoringClub, setScoringClub] = useState(null); // For club-specific scoring view
    
    // Session validation state
    const [needsReauth, setNeedsReauth] = useState(false);
    const [reauthEmail, setReauthEmail] = useState(user?.email || '');
    const [reauthCode, setReauthCode] = useState('');
    const [reauthStep, setReauthStep] = useState('email'); // 'email' | 'code'
    const [reauthLoading, setReauthLoading] = useState(false);
    const [reauthError, setReauthError] = useState('');

    useEffect(() => {
        validateAndFetch();
    }, []);
    
    // Validate session and fetch data
    const validateAndFetch = async () => {
        setLoading(true);
        try {
            // Try to fetch clubs - this will fail with 401 if session is invalid
            const res = await apiFetch('/api/superadmin/clubs');
            if (res.status === 401 || res.status === 403) {
                console.log('[SuperAdmin] Session invalid, requiring re-authentication');
                setNeedsReauth(true);
                setLoading(false);
                return;
            }
            if (res.ok) {
                const data = await res.json();
                setClubs(data);
            }
            // Also fetch stats
            await fetchSystemStats();
        } catch (err) {
            console.error('Session validation error:', err);
            setNeedsReauth(true);
        } finally {
            setLoading(false);
        }
    };

    const fetchClubs = async () => {
        try {
            const res = await apiFetch('/api/superadmin/clubs');
            if (res.status === 401 || res.status === 403) {
                setNeedsReauth(true);
                return;
            }
            if (res.ok) {
                const data = await res.json();
                setClubs(data);
            } else {
                console.error('Clubs fetch failed:', res.status);
            }
        } catch (err) {
            console.error('Clubs fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchSystemStats = async () => {
        try {
            const res = await apiFetch('/api/superadmin/stats');
            if (res.status === 401 || res.status === 403) {
                setNeedsReauth(true);
                return;
            }
            if (res.ok) {
                const data = await res.json();
                setSystemStats(data);
            } else {
                console.error('Stats fetch failed:', res.status);
            }
        } catch (err) {
            console.error('Stats fetch error:', err);
        }
    };
    
    // Re-authentication handlers
    const handleRequestCode = async () => {
        setReauthLoading(true);
        setReauthError('');
        try {
            const res = await fetch('/api/superadmin/auth/request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: reauthEmail })
            });
            if (res.ok) {
                setReauthStep('code');
            } else {
                setReauthError('Virhe koodin lähettämisessä');
            }
        } catch (err) {
            setReauthError('Verkkovirhe');
        } finally {
            setReauthLoading(false);
        }
    };
    
    const handleVerifyCode = async () => {
        setReauthLoading(true);
        setReauthError('');
        try {
            const res = await fetch('/api/superadmin/auth/verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: reauthEmail, confirmationKey: reauthCode })
            });
            if (res.ok) {
                const data = await res.json();
                // Store the new access token
                if (data.accessToken) {
                    setAccessToken(data.accessToken);
                }
                // Reset reauth state and reload data
                setNeedsReauth(false);
                setReauthStep('email');
                setReauthCode('');
                setReauthError('');
                // Reload dashboard data
                validateAndFetch();
            } else {
                const errorData = await res.json();
                setReauthError(errorData.error || 'Virheellinen koodi');
            }
        } catch (err) {
            setReauthError('Verkkovirhe');
        } finally {
            setReauthLoading(false);
        }
    };

    const createClub = async (clubData) => {
        try {
            const res = await apiFetch('/api/superadmin/clubs', {
                method: 'POST',
                body: JSON.stringify(clubData)
            });
            if (res.ok) {
                fetchClubs();
                setShowClubForm(false);
            }
        } catch (err) {
            console.error('Club create error:', err);
        }
    };

    const toggleClubStatus = async (clubId, isActive) => {
        try {
            await apiFetch(`/api/superadmin/clubs/${clubId}/status`, {
                method: 'PUT',
                body: JSON.stringify({ isActive: !isActive })
            });
            fetchClubs();
        } catch (err) {
            console.error('Club status error:', err);
        }
    };

    if (loading) {
        return (
            <div className="superadmin-loading">
                <Loader2 size={32} className="spin" />
                <span>Ladataan järjestelmän tietoja...</span>
            </div>
        );
    }
    
    // Re-authentication required
    if (needsReauth) {
        return (
            <div className="superadmin-reauth">
                <div className="reauth-card">
                    <div className="reauth-header">
                        <Lock size={32} />
                        <h2>Sessio vanhentunut</h2>
                        <p>SuperAdmin-istuntosi on vanhentunut. Vahvista henkilöllisyytesi jatkaaksesi.</p>
                    </div>
                    
                    {reauthStep === 'email' ? (
                        <div className="reauth-form">
                            <label>Sähköpostiosoite</label>
                            <input 
                                type="email" 
                                value={reauthEmail}
                                onChange={(e) => setReauthEmail(e.target.value)}
                                placeholder="superadmin@example.com"
                            />
                            <button 
                                onClick={handleRequestCode} 
                                disabled={reauthLoading || !reauthEmail}
                                className="btn-primary"
                            >
                                {reauthLoading ? (
                                    <><Loader2 size={16} className="spin" /> Lähetetään...</>
                                ) : (
                                    <><Mail size={16} /> Lähetä vahvistuskoodi</>
                                )}
                            </button>
                        </div>
                    ) : (
                        <div className="reauth-form">
                            <label>Vahvistuskoodi sähköpostista</label>
                            <input 
                                type="text" 
                                value={reauthCode}
                                onChange={(e) => setReauthCode(e.target.value)}
                                placeholder="123456"
                                maxLength={6}
                            />
                            <button 
                                onClick={handleVerifyCode} 
                                disabled={reauthLoading || reauthCode.length < 6}
                                className="btn-primary"
                            >
                                {reauthLoading ? (
                                    <><Loader2 size={16} className="spin" /> Vahvistetaan...</>
                                ) : (
                                    <><Shield size={16} /> Vahvista</>
                                )}
                            </button>
                            <button 
                                onClick={() => { setReauthStep('email'); setReauthCode(''); }}
                                className="btn-secondary"
                            >
                                Lähetä uusi koodi
                            </button>
                        </div>
                    )}
                    
                    {reauthError && (
                        <div className="reauth-error">
                            <AlertTriangle size={16} /> {reauthError}
                        </div>
                    )}
                </div>
                
                <style>{`
                    .superadmin-reauth {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        min-height: 80vh;
                        padding: 2rem;
                    }
                    
                    .reauth-card {
                        background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
                        border: 1px solid #374151;
                        border-radius: 16px;
                        padding: 2rem;
                        max-width: 400px;
                        width: 100%;
                    }
                    
                    .reauth-header {
                        text-align: center;
                        margin-bottom: 1.5rem;
                    }
                    
                    .reauth-header svg {
                        color: #f59e0b;
                        margin-bottom: 1rem;
                    }
                    
                    .reauth-header h2 {
                        color: #f0f6fc;
                        margin: 0 0 0.5rem;
                    }
                    
                    .reauth-header p {
                        color: #8b949e;
                        font-size: 0.9rem;
                        margin: 0;
                    }
                    
                    .reauth-form {
                        display: flex;
                        flex-direction: column;
                        gap: 1rem;
                    }
                    
                    .reauth-form label {
                        color: #8b949e;
                        font-size: 0.85rem;
                    }
                    
                    .reauth-form input {
                        padding: 0.75rem;
                        background: rgba(0, 0, 0, 0.3);
                        border: 1px solid #374151;
                        border-radius: 8px;
                        color: #f0f6fc;
                        font-size: 1rem;
                    }
                    
                    .reauth-form input:focus {
                        outline: none;
                        border-color: #8b5cf6;
                    }
                    
                    .reauth-form .btn-primary {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 0.5rem;
                        padding: 0.75rem;
                        background: #8b5cf6;
                        border: none;
                        border-radius: 8px;
                        color: white;
                        font-weight: 600;
                        cursor: pointer;
                        transition: background 0.2s;
                    }
                    
                    .reauth-form .btn-primary:hover:not(:disabled) {
                        background: #7c3aed;
                    }
                    
                    .reauth-form .btn-primary:disabled {
                        opacity: 0.5;
                        cursor: not-allowed;
                    }
                    
                    .reauth-form .btn-secondary {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 0.5rem;
                        padding: 0.5rem;
                        background: transparent;
                        border: 1px solid #374151;
                        border-radius: 8px;
                        color: #8b949e;
                        cursor: pointer;
                        font-size: 0.85rem;
                    }
                    
                    .reauth-form .btn-secondary:hover {
                        border-color: #8b5cf6;
                        color: #f0f6fc;
                    }
                    
                    .reauth-error {
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        margin-top: 1rem;
                        padding: 0.75rem;
                        background: rgba(239, 68, 68, 0.1);
                        border: 1px solid rgba(239, 68, 68, 0.3);
                        border-radius: 8px;
                        color: #f87171;
                        font-size: 0.9rem;
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div className="superadmin-dashboard">
            {/* Header */}
            <div className="superadmin-header">
                <div className="superadmin-title">
                    <Shield size={28} />
                    <div>
                        <h1>Super Admin</h1>
                        <span>Järjestelmänhallinta</span>
                    </div>
                </div>
            </div>

            {/* Stats Overview */}
            {systemStats && (
                <div className="stats-grid">
                    <StatCard 
                        icon={Building2} 
                        label="Klubit" 
                        value={systemStats.totalClubs} 
                        color="#8b5cf6"
                    />
                    <StatCard 
                        icon={Users} 
                        label="Käyttäjät" 
                        value={systemStats.totalUsers}
                        subtitle={`${systemStats.activeUsers} aktiivista / ${systemStats.inactiveUsers} ei-aktiivista`}
                        color="#22c55e"
                    />
                    <StatCard 
                        icon={BarChart3} 
                        label="Pelisessiot" 
                        value={systemStats.totalSessions}
                        subtitle={systemStats.sessionsToday > 0 ? `${systemStats.sessionsToday} tänään` : null}
                        color="#f59e0b"
                    />
                    <StatCard 
                        icon={Activity} 
                        label="Aktiiviset tänään" 
                        value={systemStats.activeToday}
                        subtitle="Kirjautuneet tai pelanneet"
                        color="#3b82f6"
                    />
                </div>
            )}

            {/* Tabs */}
            <div className="superadmin-tabs">
                <button 
                    className={`tab ${activeTab === 'clubs' ? 'active' : ''}`}
                    onClick={() => setActiveTab('clubs')}
                >
                    <Building2 size={16} /> Klubit
                </button>
                <button 
                    className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
                    onClick={() => setActiveTab('settings')}
                >
                    <Settings size={16} /> Järjestelmäasetukset
                </button>
                <button 
                    className={`tab ${activeTab === 'ads' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ads')}
                >
                    <Megaphone size={16} /> Globaalit mainokset
                </button>
                <button 
                    className={`tab ${activeTab === 'scoring' ? 'active' : ''}`}
                    onClick={() => setActiveTab('scoring')}
                >
                    <Calculator size={16} /> Pisteytys
                </button>
            </div>

            {/* Content */}
            <div className="superadmin-content">
                {activeTab === 'clubs' && (
                    <ClubsTab 
                        clubs={clubs}
                        onCreateClick={() => setShowClubForm(true)}
                        onViewClick={setSelectedClub}
                        onToggleStatus={toggleClubStatus}
                        onLoginAsClub={onLoginAsClub}
                        onScoringClick={(club) => {
                            setScoringClub(club);
                            setActiveTab('scoring');
                        }}
                    />
                )}
                
                {activeTab === 'settings' && (
                    <SystemSettingsTab />
                )}
                
                {activeTab === 'ads' && (
                    <GlobalAdsTab />
                )}
                
                {activeTab === 'scoring' && (
                    <div>
                        {scoringClub && (
                            <div style={{ 
                                marginBottom: '1rem', 
                                padding: '1rem', 
                                background: 'rgba(139, 92, 246, 0.1)',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <div>
                                    <span style={{ color: '#8b949e' }}>Muokkaat klubin </span>
                                    <strong style={{ color: '#a78bfa' }}>{scoringClub.name}</strong>
                                    <span style={{ color: '#8b949e' }}> pisteytystä</span>
                                </div>
                                <button 
                                    className="btn-secondary"
                                    onClick={() => setScoringClub(null)}
                                    style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                                >
                                    Takaisin globaaleihin
                                </button>
                            </div>
                        )}
                        <ScoringStrategyManager 
                            title={scoringClub ? `${scoringClub.name} - Pisteytysasetukset` : "Globaalit Pisteytysmallit"}
                            clubId={scoringClub?.id || null}
                        />
                    </div>
                )}
            </div>

            {/* Club Details Modal */}
            {selectedClub && (
                <ClubDetailsModal 
                    club={selectedClub}
                    onClose={() => setSelectedClub(null)}
                    onRefresh={fetchClubs}
                />
            )}

            {/* Create Club Modal */}
            {showClubForm && (
                <CreateClubModal 
                    onClose={() => setShowClubForm(false)}
                    onCreate={createClub}
                />
            )}

            <style>{`
                .superadmin-dashboard {
                    padding: 1.5rem;
                    max-width: 1400px;
                    margin: 0 auto;
                    min-height: 100vh;
                }

                .superadmin-loading {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 1rem;
                    padding: 3rem;
                    color: #8b949e;
                }

                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { to { transform: rotate(360deg); } }

                .superadmin-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 2rem;
                    padding-bottom: 1rem;
                    border-bottom: 2px solid #30363d;
                }

                .superadmin-title {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    color: #f0f6fc;
                }

                .superadmin-title h1 {
                    margin: 0;
                    font-size: 1.5rem;
                }

                .superadmin-title span {
                    color: #8b949e;
                    font-size: 0.85rem;
                }

                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 1rem;
                    margin-bottom: 2rem;
                }

                .stat-card {
                    background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
                    border: 1px solid #374151;
                    border-radius: 12px;
                    padding: 1.25rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }

                .stat-card-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .stat-card-value {
                    font-size: 1.75rem;
                    font-weight: 700;
                    color: #f0f6fc;
                }

                .stat-card-label {
                    font-size: 0.85rem;
                    color: #8b949e;
                }

                .stat-card-subtitle {
                    font-size: 0.75rem;
                    color: #6b7280;
                    margin-top: 0.25rem;
                }

                .superadmin-tabs {
                    display: flex;
                    gap: 0.5rem;
                    margin-bottom: 1.5rem;
                    border-bottom: 1px solid #30363d;
                    padding-bottom: 0.5rem;
                }

                .tab {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.25rem;
                    background: transparent;
                    border: none;
                    color: #8b949e;
                    cursor: pointer;
                    border-radius: 8px 8px 0 0;
                    transition: all 0.2s;
                }

                .tab:hover {
                    background: rgba(255, 255, 255, 0.05);
                    color: #f0f6fc;
                }

                .tab.active {
                    background: rgba(139, 92, 246, 0.1);
                    color: #a78bfa;
                    border-bottom: 2px solid #8b5cf6;
                }

                .superadmin-content {
                    background: #161b22;
                    border: 1px solid #30363d;
                    border-radius: 12px;
                    padding: 1.5rem;
                }

                /* Clubs Table */
                .clubs-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.5rem;
                }

                .clubs-header h3 {
                    margin: 0;
                    color: #f0f6fc;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .clubs-table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .clubs-table th,
                .clubs-table td {
                    padding: 0.75rem;
                    text-align: left;
                    border-bottom: 1px solid #30363d;
                }

                .clubs-table th {
                    color: #8b949e;
                    font-size: 0.8rem;
                    text-transform: uppercase;
                    font-weight: 600;
                }

                .clubs-table td {
                    color: #c9d1d9;
                }

                .clubs-table tr:hover td {
                    background: rgba(255, 255, 255, 0.02);
                }

                .club-name {
                    font-weight: 600;
                    color: #f0f6fc;
                }

                .club-slug {
                    font-size: 0.8rem;
                    color: #8b949e;
                }

                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    padding: 0.25rem 0.5rem;
                    border-radius: 12px;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                .status-badge.active {
                    background: rgba(34, 197, 94, 0.15);
                    color: #22c55e;
                }

                .status-badge.inactive {
                    background: rgba(239, 68, 68, 0.15);
                    color: #ef4444;
                }

                .action-btns {
                    display: flex;
                    gap: 0.5rem;
                }

                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 6px;
                    border: 1px solid #374151;
                    background: transparent;
                    color: #8b949e;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                }

                .action-btn:hover {
                    background: rgba(255, 255, 255, 0.1);
                    color: #f0f6fc;
                }

                .action-btn.primary:hover {
                    background: rgba(139, 92, 246, 0.2);
                    border-color: #8b5cf6;
                    color: #a78bfa;
                }

                .btn-primary {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.6rem 1rem;
                    background: #8b5cf6;
                    border: none;
                    border-radius: 8px;
                    color: #fff;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                }

                .btn-primary:hover {
                    background: #7c3aed;
                }

                /* Modal */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.8);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 10000;
                    padding: 1rem;
                }

                .modal-content {
                    background: #161b22;
                    border: 1px solid #30363d;
                    border-radius: 16px;
                    max-width: 600px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1.25rem;
                    border-bottom: 1px solid #30363d;
                }

                .modal-header h3 {
                    margin: 0;
                    color: #f0f6fc;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .modal-close {
                    background: rgba(255, 255, 255, 0.1);
                    border: none;
                    color: #9ca3af;
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .modal-close:hover {
                    background: rgba(255, 255, 255, 0.2);
                    color: #fff;
                }

                .modal-body {
                    padding: 1.25rem;
                }

                .modal-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 0.75rem;
                    padding: 1.25rem;
                    border-top: 1px solid #30363d;
                }

                .form-group {
                    margin-bottom: 1rem;
                }

                .form-group label {
                    display: block;
                    margin-bottom: 0.5rem;
                    color: #c9d1d9;
                    font-size: 0.9rem;
                }

                .form-group input,
                .form-group select,
                .form-group textarea {
                    width: 100%;
                    padding: 0.75rem;
                    background: #0d1117;
                    border: 1px solid #30363d;
                    border-radius: 8px;
                    color: #c9d1d9;
                    font-size: 0.9rem;
                }

                .form-group input:focus,
                .form-group select:focus,
                .form-group textarea:focus {
                    outline: none;
                    border-color: #8b5cf6;
                }

                .btn-secondary {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.6rem 1rem;
                    background: #21262d;
                    border: 1px solid #30363d;
                    border-radius: 8px;
                    color: #c9d1d9;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .btn-secondary:hover {
                    background: #30363d;
                }

                @media (max-width: 768px) {
                    .superadmin-dashboard {
                        padding: 1rem;
                    }

                    .stats-grid {
                        grid-template-columns: 1fr 1fr;
                    }

                    .superadmin-tabs {
                        flex-wrap: wrap;
                    }

                    .clubs-table {
                        font-size: 0.85rem;
                    }

                    .clubs-table th:nth-child(3),
                    .clubs-table td:nth-child(3),
                    .clubs-table th:nth-child(4),
                    .clubs-table td:nth-child(4) {
                        display: none;
                    }
                }
            `}</style>
        </div>
    );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subtitle, color }) => (
    <div className="stat-card">
        <div className="stat-card-icon" style={{ background: `${color}20` }}>
            <Icon size={24} style={{ color }} />
        </div>
        <div>
            <div className="stat-card-value">{value}</div>
            <div className="stat-card-label">{label}</div>
            {subtitle && <div className="stat-card-subtitle">{subtitle}</div>}
        </div>
    </div>
);

// Clubs Tab with search
const ClubsTab = ({ clubs, onCreateClick, onViewClick, onToggleStatus, onLoginAsClub, onScoringClick }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [displayLimit, setDisplayLimit] = useState(50);
    
    const filteredClubs = clubs.filter(club => 
        club.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        club.slug.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const displayedClubs = filteredClubs.slice(0, displayLimit);
    const hasMore = filteredClubs.length > displayLimit;
    
    return (
        <div>
            <div className="clubs-header">
                <h3><Building2 size={20} /> Rekisteröidyt klubit ({clubs.length})</h3>
                <button className="btn-primary" onClick={onCreateClick}>
                    <Plus size={16} /> Luo uusi klubi
                </button>
            </div>
            
            {/* Search */}
            <div style={{ marginBottom: '1rem' }}>
                <input 
                    type="text"
                    placeholder="Hae nimen tai slugin perusteella..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    style={{
                        width: '100%',
                        maxWidth: '400px',
                        padding: '0.75rem 1rem',
                        background: '#0d1117',
                        border: '1px solid #30363d',
                        borderRadius: '8px',
                        color: '#c9d1d9',
                        fontSize: '0.9rem'
                    }}
                />
                {searchTerm && (
                    <span style={{ marginLeft: '1rem', color: '#8b949e' }}>
                        {filteredClubs.length} klubia löytyi
                    </span>
                )}
            </div>
            
            <table className="clubs-table">
                <thead>
                    <tr>
                        <th>Klubi</th>
                        <th>Jäsenet</th>
                        <th>Pelit</th>
                        <th>Luotu</th>
                        <th>Status</th>
                        <th>Toiminnot</th>
                    </tr>
                </thead>
                <tbody>
                    {displayedClubs.map(club => (
                        <tr key={club.id}>
                            <td>
                                <div className="club-name">{club.name}</div>
                                <div className="club-slug">/{club.slug}</div>
                            </td>
                            <td>{club.memberCount || 0}</td>
                            <td>{club.gameCount || 0}</td>
                            <td>{new Date(club.created_at).toLocaleDateString('fi-FI')}</td>
                            <td>
                                <span className={`status-badge ${club.is_active ? 'active' : 'inactive'}`}>
                                    {club.is_active ? 'Aktiivinen' : 'Ei käytössä'}
                                </span>
                            </td>
                            <td>
                                <div className="action-btns">
                                    <button 
                                        className="action-btn" 
                                        title="Näytä tiedot"
                                        onClick={() => onViewClick(club)}
                                    >
                                        <Eye size={16} />
                                    </button>
                                    <button 
                                        className="action-btn" 
                                        title="Pisteytys"
                                        onClick={() => onScoringClick && onScoringClick(club)}
                                    >
                                        <Calculator size={16} />
                                    </button>
                                    <button 
                                        className="action-btn primary" 
                                        title="Kirjaudu klubin adminina"
                                        onClick={() => onLoginAsClub && onLoginAsClub(club.id)}
                                    >
                                        <LogIn size={16} />
                                    </button>
                                    <button 
                                        className="action-btn" 
                                        title={club.is_active ? 'Deaktivoi' : 'Aktivoi'}
                                        onClick={() => onToggleStatus(club.id, club.is_active)}
                                    >
                                        {club.is_active ? <AlertTriangle size={16} /> : <Activity size={16} />}
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            
            {hasMore && (
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                    <button 
                        className="btn-secondary"
                        onClick={() => setDisplayLimit(prev => prev + 50)}
                    >
                        Näytä lisää ({filteredClubs.length - displayLimit} jäljellä)
                    </button>
                </div>
            )}
        </div>
    );
};

// System Settings Tab
const SystemSettingsTab = () => {
    const [activeSection, setActiveSection] = useState('smtp');
    
    const tabStyle = (section) => ({
        padding: '0.5rem 1rem',
        background: activeSection === section ? 'rgba(139, 92, 246, 0.2)' : 'rgba(0, 0, 0, 0.2)',
        border: `1px solid ${activeSection === section ? '#8b5cf6' : '#374151'}`,
        borderRadius: '6px',
        color: activeSection === section ? '#a78bfa' : '#8b949e',
        cursor: 'pointer'
    });
    
    return (
        <div>
            <h3 style={{ color: '#f0f6fc', marginBottom: '1rem' }}>
                <Settings size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Globaalit järjestelmäasetukset
            </h3>
            <p style={{ color: '#8b949e', marginBottom: '1.5rem' }}>
                Nämä asetukset vaikuttavat kaikkiin klubeihin.
            </p>
            
            {/* Settings Sections */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                <button onClick={() => setActiveSection('smtp')} style={tabStyle('smtp')}>
                    📧 Sähköposti (SMTP)
                </button>
                <button onClick={() => setActiveSection('ai')} style={tabStyle('ai')}>
                    🤖 AI-asetukset
                </button>
                <button onClick={() => setActiveSection('prompts')} style={tabStyle('prompts')}>
                    💬 Promptit
                </button>
                <button onClick={() => setActiveSection('general')} style={tabStyle('general')}>
                    ⚙️ Yleiset
                </button>
            </div>
            
            {activeSection === 'smtp' && <SMTPSettingsSection />}
            {activeSection === 'ai' && <AISettingsSection />}
            {activeSection === 'prompts' && <PromptsSettingsSection />}
            {activeSection === 'general' && <GeneralSettingsSection />}
        </div>
    );
};

// SMTP Settings Section
const SMTPSettingsSection = () => {
    const [smtpSettings, setSmtpSettings] = useState({
        smtp_host: '',
        smtp_port: '465',
        smtp_user: '',
        smtp_password: '',
        smtp_secure: 'true',
        smtp_from_name: '',
        smtp_from_email: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        fetchSMTPSettings();
    }, []);

    const fetchSMTPSettings = async () => {
        try {
            const res = await apiFetch('/api/superadmin/settings/smtp');
            if (res.ok) {
                const data = await res.json();
                setSmtpSettings(data);
            }
        } catch (err) {
            console.error('SMTP fetch error:', err);
            setMessage({ type: 'error', text: 'Virhe asetuksien hakemisessa' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage({ type: '', text: '' });
        
        try {
            const res = await apiFetch('/api/superadmin/settings/smtp', {
                method: 'PUT',
                body: JSON.stringify(smtpSettings)
            });
            
            if (res.ok) {
                setMessage({ type: 'success', text: '✅ Asetukset tallennettu!' });
            } else {
                const data = await res.json();
                setMessage({ type: 'error', text: data.error || 'Tallentaminen epäonnistui' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Verkkovirhe' });
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        if (!testEmail) {
            setMessage({ type: 'error', text: 'Syötä testisähköpostiosoite' });
            return;
        }
        
        setTesting(true);
        setMessage({ type: '', text: '' });
        
        try {
            const res = await apiFetch('/api/superadmin/settings/smtp/test', {
                method: 'POST',
                body: JSON.stringify({ email: testEmail })
            });
            
            const data = await res.json();
            
            if (res.ok) {
                setMessage({ type: 'success', text: `✅ Testisähköposti lähetetty osoitteeseen ${testEmail}` });
            } else {
                setMessage({ type: 'error', text: data.error || 'Testi epäonnistui' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Verkkovirhe' });
        } finally {
            setTesting(false);
        }
    };

    const handleChange = (field, value) => {
        setSmtpSettings(prev => ({ ...prev, [field]: value }));
    };

    if (loading) return <p style={{ color: '#8b949e' }}>Ladataan SMTP-asetuksia...</p>;

    const inputStyle = {
        width: '100%',
        padding: '0.75rem',
        background: 'rgba(0, 0, 0, 0.3)',
        border: '1px solid #374151',
        borderRadius: '6px',
        color: '#f0f6fc',
        fontSize: '0.95rem'
    };

    const labelStyle = {
        display: 'block',
        color: '#8b949e',
        marginBottom: '0.5rem',
        fontSize: '0.85rem'
    };

    return (
        <div style={{ maxWidth: '600px' }}>
            <div style={{
                background: 'rgba(0, 0, 0, 0.2)',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid #374151'
            }}>
                <h4 style={{ color: '#f0f6fc', marginTop: 0, marginBottom: '1rem' }}>
                    📧 SMTP-sähköpostiasetukset
                </h4>
                <p style={{ color: '#8b949e', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
                    Käytetään SuperAdmin-vahvistuskoodien ja ilmoitusten lähettämiseen.
                </p>

                <div style={{ display: 'grid', gap: '1rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
                        <div>
                            <label style={labelStyle}>SMTP-palvelin</label>
                            <input
                                type="text"
                                value={smtpSettings.smtp_host}
                                onChange={(e) => handleChange('smtp_host', e.target.value)}
                                placeholder="mail.example.com"
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>Portti</label>
                            <input
                                type="text"
                                value={smtpSettings.smtp_port}
                                onChange={(e) => handleChange('smtp_port', e.target.value)}
                                placeholder="465"
                                style={inputStyle}
                            />
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                            <label style={labelStyle}>Käyttäjätunnus</label>
                            <input
                                type="text"
                                value={smtpSettings.smtp_user}
                                onChange={(e) => handleChange('smtp_user', e.target.value)}
                                placeholder="user@example.com"
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>Salasana</label>
                            <input
                                type="password"
                                value={smtpSettings.smtp_password}
                                onChange={(e) => handleChange('smtp_password', e.target.value)}
                                placeholder="••••••••"
                                style={inputStyle}
                            />
                        </div>
                    </div>

                    <div>
                        <label style={labelStyle}>SSL/TLS</label>
                        <select
                            value={smtpSettings.smtp_secure}
                            onChange={(e) => handleChange('smtp_secure', e.target.value)}
                            style={inputStyle}
                        >
                            <option value="true">Käytä SSL/TLS (portti 465)</option>
                            <option value="false">STARTTLS (portti 587)</option>
                        </select>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                            <label style={labelStyle}>Lähettäjän nimi</label>
                            <input
                                type="text"
                                value={smtpSettings.smtp_from_name}
                                onChange={(e) => handleChange('smtp_from_name', e.target.value)}
                                placeholder="Masan Peliklubi"
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>Lähettäjän sähköposti</label>
                            <input
                                type="email"
                                value={smtpSettings.smtp_from_email}
                                onChange={(e) => handleChange('smtp_from_email', e.target.value)}
                                placeholder="noreply@example.com"
                                style={inputStyle}
                            />
                        </div>
                    </div>
                </div>

                {/* Message */}
                {message.text && (
                    <div style={{
                        marginTop: '1rem',
                        padding: '0.75rem',
                        borderRadius: '6px',
                        background: message.type === 'success' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        border: `1px solid ${message.type === 'success' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                        color: message.type === 'success' ? '#22c55e' : '#ef4444'
                    }}>
                        {message.text}
                    </div>
                )}

                {/* Save Button */}
                <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                        marginTop: '1.5rem',
                        width: '100%',
                        padding: '0.75rem',
                        background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        fontWeight: '500',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        opacity: saving ? 0.7 : 1
                    }}
                >
                    {saving ? 'Tallennetaan...' : 'Tallenna asetukset'}
                </button>

                {/* Test Section */}
                <div style={{
                    marginTop: '1.5rem',
                    paddingTop: '1.5rem',
                    borderTop: '1px solid #374151'
                }}>
                    <h5 style={{ color: '#f0f6fc', marginTop: 0, marginBottom: '0.75rem' }}>
                        🧪 Testaa SMTP-yhteys
                    </h5>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                            type="email"
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                            placeholder="Testisähköpostiosoite"
                            style={{ ...inputStyle, flex: 1 }}
                        />
                        <button
                            onClick={handleTest}
                            disabled={testing}
                            style={{
                                padding: '0.75rem 1.5rem',
                                background: 'rgba(34, 197, 94, 0.2)',
                                border: '1px solid rgba(34, 197, 94, 0.3)',
                                borderRadius: '6px',
                                color: '#22c55e',
                                cursor: testing ? 'not-allowed' : 'pointer',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {testing ? 'Testataan...' : 'Lähetä testi'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// AI Settings Section
const AISettingsSection = () => {
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState({});
    const [editValues, setEditValues] = useState({});
    const [testResult, setTestResult] = useState(null);
    const [testing, setTesting] = useState(false);
    const [revealedKeys, setRevealedKeys] = useState({});

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await apiFetch('/api/admin/settings');
                if (res.ok) {
                    const data = await res.json();
                    const aiSettings = data.filter(s => s.category === 'ai');
                    setSettings(aiSettings);
                    const values = {};
                    aiSettings.forEach(s => { values[s.setting_key] = s.setting_value; });
                    setEditValues(values);
                }
            } catch (err) {
                console.error('AI settings fetch error:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const handleSave = async (key) => {
        setSaving(prev => ({ ...prev, [key]: true }));
        try {
            const res = await apiFetch(`/api/admin/settings/${key}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ value: editValues[key] })
            });
            if (res.ok) {
                setSaving(prev => ({ ...prev, [key]: 'success' }));
                setTimeout(() => setSaving(prev => ({ ...prev, [key]: false })), 1500);
            }
        } catch (err) {
            console.error('Save error:', err);
            setSaving(prev => ({ ...prev, [key]: 'error' }));
        }
    };

    const handleReveal = async (key) => {
        if (revealedKeys[key]) {
            setRevealedKeys(prev => ({ ...prev, [key]: false }));
            return;
        }
        try {
            const res = await apiFetch(`/api/admin/settings/${key}?reveal=true`);
            const data = await res.json();
            setEditValues(prev => ({ ...prev, [key]: data.setting_value }));
            setRevealedKeys(prev => ({ ...prev, [key]: true }));
        } catch (err) {
            console.error('Reveal error:', err);
        }
    };

    const testAI = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const res = await apiFetch('/api/admin/ai-test');
            const data = await res.json();
            setTestResult(data);
        } catch (err) {
            setTestResult({ success: false, error: err.message });
        } finally {
            setTesting(false);
        }
    };

    if (loading) return <p style={{ color: '#8b949e' }}>Ladataan AI-asetuksia...</p>;

    return (
        <div>
            {/* AI Test */}
            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', border: '1px solid #374151' }}>
                <h4 style={{ color: '#f0f6fc', margin: '0 0 1rem' }}>🧪 Testaa AI-yhteyttä</h4>
                <button 
                    onClick={testAI} 
                    disabled={testing}
                    style={{
                        padding: '0.5rem 1rem',
                        background: '#8b5cf6',
                        border: 'none',
                        borderRadius: '6px',
                        color: 'white',
                        cursor: testing ? 'wait' : 'pointer'
                    }}
                >
                    {testing ? 'Testataan...' : 'Testaa yhteys'}
                </button>
                {testResult && (
                    <div style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        background: testResult.success ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        border: `1px solid ${testResult.success ? '#22c55e' : '#ef4444'}`,
                        borderRadius: '6px',
                        color: testResult.success ? '#22c55e' : '#ef4444'
                    }}>
                        {testResult.success ? (
                            <>
                                <strong>✓ Yhteys toimii!</strong>
                                <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem' }}>Malli: {testResult.model}</p>
                            </>
                        ) : (
                            <>
                                <strong>✗ Yhteys epäonnistui</strong>
                                <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem' }}>{testResult.error}</p>
                            </>
                        )}
                    </div>
                )}
            </div>
            
            {/* AI Settings List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {settings.map(s => (
                    <div key={s.setting_key} style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid #374151'
                    }}>
                        <div style={{ marginBottom: '0.5rem' }}>
                            <span style={{ color: '#8b949e', fontSize: '0.85rem' }}>{s.description || s.setting_key}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {s.is_sensitive ? (
                                <>
                                    <input
                                        type={revealedKeys[s.setting_key] ? 'text' : 'password'}
                                        value={editValues[s.setting_key] || ''}
                                        onChange={e => setEditValues(prev => ({ ...prev, [s.setting_key]: e.target.value }))}
                                        style={{
                                            flex: 1,
                                            padding: '0.5rem',
                                            background: '#0d1117',
                                            border: '1px solid #30363d',
                                            borderRadius: '6px',
                                            color: '#c9d1d9'
                                        }}
                                    />
                                    <button 
                                        onClick={() => handleReveal(s.setting_key)}
                                        style={{ padding: '0.5rem 0.75rem', background: '#21262d', border: '1px solid #30363d', borderRadius: '6px', color: '#8b949e', cursor: 'pointer' }}
                                    >
                                        {revealedKeys[s.setting_key] ? '🙈' : '👁️'}
                                    </button>
                                </>
                            ) : (
                                <input
                                    type="text"
                                    value={editValues[s.setting_key] || ''}
                                    onChange={e => setEditValues(prev => ({ ...prev, [s.setting_key]: e.target.value }))}
                                    style={{
                                        flex: 1,
                                        padding: '0.5rem',
                                        background: '#0d1117',
                                        border: '1px solid #30363d',
                                        borderRadius: '6px',
                                        color: '#c9d1d9'
                                    }}
                                />
                            )}
                            <button 
                                onClick={() => handleSave(s.setting_key)}
                                disabled={saving[s.setting_key] === true}
                                style={{ 
                                    padding: '0.5rem 1rem', 
                                    background: saving[s.setting_key] === 'success' ? '#22c55e' : '#238636', 
                                    border: 'none', 
                                    borderRadius: '6px', 
                                    color: 'white', 
                                    cursor: 'pointer' 
                                }}
                            >
                                {saving[s.setting_key] === 'success' ? '✓' : 'Tallenna'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            
            {/* AI Help */}
            <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '8px' }}>
                <h4 style={{ color: '#60a5fa', margin: '0 0 0.5rem' }}>💡 Vinkkejä</h4>
                <ul style={{ color: '#8b949e', margin: 0, paddingLeft: '1.25rem', fontSize: '0.9rem' }}>
                    <li><strong>gemini-2.0-flash</strong> - Nopein ja halvin Gemini-malli</li>
                    <li><strong>gemini-pro</strong> - Vanhempi vakaa malli</li>
                    <li><strong>gemini-1.5-pro</strong> - Tehokkaampi malli (kalliimpi)</li>
                </ul>
            </div>
        </div>
    );
};

// Prompts Settings Section
const PromptsSettingsSection = () => {
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState({});
    const [editValues, setEditValues] = useState({});

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await apiFetch('/api/admin/settings');
                if (res.ok) {
                    const data = await res.json();
                    const promptSettings = data.filter(s => s.category === 'prompts');
                    setSettings(promptSettings);
                    const values = {};
                    promptSettings.forEach(s => { values[s.setting_key] = s.setting_value; });
                    setEditValues(values);
                }
            } catch (err) {
                console.error('Prompts settings fetch error:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const handleSave = async (key) => {
        setSaving(prev => ({ ...prev, [key]: true }));
        try {
            const res = await apiFetch(`/api/admin/settings/${key}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ value: editValues[key] })
            });
            if (res.ok) {
                setSaving(prev => ({ ...prev, [key]: 'success' }));
                setTimeout(() => setSaving(prev => ({ ...prev, [key]: false })), 1500);
            }
        } catch (err) {
            console.error('Save error:', err);
            setSaving(prev => ({ ...prev, [key]: 'error' }));
        }
    };

    if (loading) return <p style={{ color: '#8b949e' }}>Ladataan prompteja...</p>;

    return (
        <div>
            <p style={{ color: '#8b949e', marginBottom: '1rem' }}>
                AI-promptit eri käyttötarkoituksiin. Näitä käytetään AI-avusteisissa toiminnoissa.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {settings.map(s => (
                    <div key={s.setting_key} style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid #374151'
                    }}>
                        <div style={{ marginBottom: '0.5rem' }}>
                            <span style={{ color: '#f0f6fc', fontWeight: '500' }}>{s.description || s.setting_key}</span>
                        </div>
                        <textarea
                            value={editValues[s.setting_key] || ''}
                            onChange={e => setEditValues(prev => ({ ...prev, [s.setting_key]: e.target.value }))}
                            rows={8}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                background: '#0d1117',
                                border: '1px solid #30363d',
                                borderRadius: '6px',
                                color: '#c9d1d9',
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                                resize: 'vertical'
                            }}
                        />
                        <div style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                            <button 
                                onClick={() => handleSave(s.setting_key)}
                                disabled={saving[s.setting_key] === true}
                                style={{ 
                                    padding: '0.5rem 1rem', 
                                    background: saving[s.setting_key] === 'success' ? '#22c55e' : '#238636', 
                                    border: 'none', 
                                    borderRadius: '6px', 
                                    color: 'white', 
                                    cursor: 'pointer' 
                                }}
                            >
                                {saving[s.setting_key] === 'success' ? '✓ Tallennettu' : 'Tallenna'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// General Settings Section
const GeneralSettingsSection = () => {
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await apiFetch('/api/settings');
                if (res.ok) {
                    const data = await res.json();
                    setSettings(data);
                }
            } catch (err) {
                console.error('Settings fetch error:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    if (loading) return <p style={{ color: '#8b949e' }}>Ladataan asetuksia...</p>;

    return (
        <div>
            <p style={{ color: '#8b949e', marginBottom: '1rem' }}>
                Muut järjestelmäasetukset (BGG API, Gemini AI, jne.)
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {settings.map(s => (
                    <div key={s.key} style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid #374151'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <strong style={{ color: '#f0f6fc' }}>{s.key}</strong>
                                <p style={{ color: '#8b949e', fontSize: '0.85rem', margin: '0.25rem 0 0' }}>
                                    {s.description || 'Ei kuvausta'}
                                </p>
                            </div>
                            <span style={{ 
                                color: '#a78bfa',
                                background: 'rgba(139, 92, 246, 0.1)',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '6px',
                                fontSize: '0.85rem'
                            }}>
                                {s.value}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Global Ads Tab
const GlobalAdsTab = () => (
    <div>
        <h3 style={{ color: '#f0f6fc', marginBottom: '1rem' }}>
            <Megaphone size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            Globaalit mainoskampanjat
        </h3>
        <p style={{ color: '#8b949e', marginBottom: '1.5rem' }}>
            Hallitse mainossponsoreita ja tuotteita, jotka näkyvät kaikissa klubeissa.
        </p>
        <div style={{
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '8px',
            padding: '1rem',
            color: '#f59e0b'
        }}>
            <AlertTriangle size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            Mainosten massahallinta toteutetaan myöhemmässä versiossa.
            Tällä hetkellä käytä klubikohtaista Admin → Mainosten hallinta -näkymää.
        </div>
    </div>
);

// Club Details Modal (includes per-club beta feature toggles)
const ClubDetailsModal = ({ club, onClose, onRefresh }) => {
    const [catalog, setCatalog] = useState([]);
    const [enabled, setEnabled] = useState({});
    const [loadingFeatures, setLoadingFeatures] = useState(true);
    const [savingFeature, setSavingFeature] = useState(null);
    const [featureError, setFeatureError] = useState('');

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoadingFeatures(true);
            setFeatureError('');
            try {
                const res = await apiFetch(`/api/superadmin/clubs/${club.id}/beta-features`);
                if (!res.ok) {
                    throw new Error('Beta-toimintojen lataus epäonnistui');
                }
                const data = await res.json();
                if (!cancelled) {
                    setCatalog(data.catalog || []);
                    setEnabled(data.enabled || {});
                }
            } catch (err) {
                console.error(err);
                if (!cancelled) setFeatureError(err.message || 'Virhe');
            } finally {
                if (!cancelled) setLoadingFeatures(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [club.id]);

    const toggleFeature = async (featureId) => {
        const next = !enabled[featureId];
        setSavingFeature(featureId);
        setFeatureError('');
        try {
            const res = await apiFetch(`/api/superadmin/clubs/${club.id}/beta-features`, {
                method: 'PUT',
                body: JSON.stringify({ featureId, enabled: next })
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || 'Tallennus epäonnistui');
            }
            const data = await res.json();
            setEnabled(data.enabled || {});
            if (onRefresh) onRefresh();
        } catch (err) {
            console.error(err);
            setFeatureError(err.message || 'Tallennus epäonnistui');
        } finally {
            setSavingFeature(null);
        }
    };

    return (
    <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
                <h3><Building2 size={20} /> {club.name}</h3>
                <button className="modal-close" onClick={onClose}>
                    <X size={18} />
                </button>
            </div>
            <div className="modal-body">
                <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                        <label style={{ color: '#8b949e', fontSize: '0.8rem' }}>Slug</label>
                        <p style={{ color: '#f0f6fc', margin: '0.25rem 0' }}>/{club.slug}</p>
                    </div>
                    <div>
                        <label style={{ color: '#8b949e', fontSize: '0.8rem' }}>Kuvaus</label>
                        <p style={{ color: '#f0f6fc', margin: '0.25rem 0' }}>{club.description || '-'}</p>
                    </div>
                    <div>
                        <label style={{ color: '#8b949e', fontSize: '0.8rem' }}>Kieli</label>
                        <p style={{ color: '#f0f6fc', margin: '0.25rem 0' }}>{club.language?.toUpperCase() || 'FI'}</p>
                    </div>
                    <div>
                        <label style={{ color: '#8b949e', fontSize: '0.8rem' }}>Luotu</label>
                        <p style={{ color: '#f0f6fc', margin: '0.25rem 0' }}>
                            {new Date(club.created_at).toLocaleString('fi-FI')}
                        </p>
                    </div>
                    <div>
                        <label style={{ color: '#8b949e', fontSize: '0.8rem' }}>Väri</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div style={{ 
                                width: '24px', 
                                height: '24px', 
                                borderRadius: '4px',
                                background: club.primary_color || '#8b5cf6'
                            }} />
                            <span style={{ color: '#f0f6fc' }}>{club.primary_color || '#8b5cf6'}</span>
                        </div>
                    </div>

                    <div style={{
                        marginTop: '0.5rem',
                        paddingTop: '1rem',
                        borderTop: '1px solid #30363d'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                            <FlaskConical size={18} color="#a78bfa" />
                            <label style={{ color: '#f0f6fc', fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>
                                Beta-toiminnot
                            </label>
                        </div>
                        <p style={{ color: '#8b949e', fontSize: '0.8rem', margin: '0 0 0.75rem' }}>
                            Uudet toiminnot pysyvät pois päältä kunnes aktivoit ne tälle klubille.
                        </p>
                        {loadingFeatures ? (
                            <div style={{ color: '#8b949e', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Loader2 size={16} className="spin" /> Ladataan...
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gap: '0.75rem' }}>
                                {catalog.map((feature) => {
                                    const isOn = !!enabled[feature.id];
                                    const isSaving = savingFeature === feature.id;
                                    return (
                                        <div
                                            key={feature.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'space-between',
                                                gap: '1rem',
                                                padding: '0.75rem',
                                                background: 'rgba(0,0,0,0.25)',
                                                borderRadius: '8px',
                                                border: '1px solid #30363d'
                                            }}
                                        >
                                            <div>
                                                <div style={{ color: '#f0f6fc', fontWeight: 500 }}>{feature.name}</div>
                                                <div style={{ color: '#8b949e', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                                                    {feature.description}
                                                </div>
                                                <code style={{ color: '#6e7681', fontSize: '0.7rem' }}>{feature.id}</code>
                                            </div>
                                            <button
                                                type="button"
                                                disabled={isSaving}
                                                onClick={() => toggleFeature(feature.id)}
                                                title={isOn ? 'Poista käytöstä' : 'Aktivoi'}
                                                style={{
                                                    flexShrink: 0,
                                                    minWidth: '72px',
                                                    padding: '0.4rem 0.75rem',
                                                    borderRadius: '6px',
                                                    border: `1px solid ${isOn ? '#238636' : '#30363d'}`,
                                                    background: isOn ? 'rgba(35, 134, 54, 0.25)' : 'rgba(0,0,0,0.3)',
                                                    color: isOn ? '#3fb950' : '#8b949e',
                                                    cursor: isSaving ? 'wait' : 'pointer',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {isSaving ? <Loader2 size={14} className="spin" /> : (isOn ? 'Päällä' : 'Pois')}
                                            </button>
                                        </div>
                                    );
                                })}
                                {catalog.length === 0 && (
                                    <p style={{ color: '#8b949e', margin: 0 }}>Ei rekisteröityjä beta-toimintoja.</p>
                                )}
                            </div>
                        )}
                        {featureError && (
                            <p style={{ color: '#f85149', fontSize: '0.85rem', marginTop: '0.75rem' }}>{featureError}</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </div>
    );
};

// Create Club Modal
const CreateClubModal = ({ onClose, onCreate }) => {
    const [name, setName] = useState('');
    const [slug, setSlug] = useState('');
    const [description, setDescription] = useState('');
    const [language, setLanguage] = useState('fi');
    const [saving, setSaving] = useState(false);

    const generateSlug = (text) => {
        return text
            .toLowerCase()
            .replace(/ä/g, 'a')
            .replace(/ö/g, 'o')
            .replace(/å/g, 'a')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
    };

    const handleNameChange = (e) => {
        const newName = e.target.value;
        setName(newName);
        setSlug(generateSlug(newName));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name || !slug) return;
        
        setSaving(true);
        await onCreate({ name, slug, description, language });
        setSaving(false);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3><Plus size={20} /> Luo uusi klubi</h3>
                    <button className="modal-close" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        <div className="form-group">
                            <label>Klubin nimi *</label>
                            <input 
                                type="text" 
                                value={name} 
                                onChange={handleNameChange}
                                placeholder="esim. Turun Noppanikkari"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>URL-polku (slug) *</label>
                            <input 
                                type="text" 
                                value={slug} 
                                onChange={e => setSlug(e.target.value)}
                                placeholder="esim. turun-noppanikkari"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Kuvaus</label>
                            <textarea 
                                value={description} 
                                onChange={e => setDescription(e.target.value)}
                                placeholder="Lyhyt kuvaus klubista..."
                                rows={3}
                            />
                        </div>
                        <div className="form-group">
                            <label>Kieli</label>
                            <select value={language} onChange={e => setLanguage(e.target.value)}>
                                <option value="fi">Suomi</option>
                                <option value="en">English</option>
                                <option value="sv">Svenska</option>
                            </select>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn-secondary" onClick={onClose}>
                            Peruuta
                        </button>
                        <button type="submit" className="btn-primary" disabled={saving}>
                            {saving ? <Loader2 size={16} className="spin" /> : <Plus size={16} />}
                            Luo klubi
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SuperAdminDashboard;

